import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import {
  Calendar, CheckCircle, XCircle, Users, Clock, Sparkles,
  FileText, Megaphone, Network, User, BarChart, Settings, Zap, Copy, School, KeyRound, X, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const QuickActions = ({ isAdmin, role, onChangePwd }) => {
  const { t } = useLanguage();

  const actions = [
    { label: t('apply_for_leave'), icon: Calendar, path: '/dashboard/leave', gradient: 'from-teal-500 to-emerald-500', show: true },
    { label: t('nav_my_profile'), icon: User, path: '/dashboard/profile', gradient: 'from-indigo-500 to-purple-500', show: true },
    { label: t('nav_org_chart'), icon: Network, path: '/dashboard/org-chart', gradient: 'from-violet-500 to-purple-500', show: true },
    { label: t('nav_approvals'), icon: FileText, path: '/dashboard/requests', gradient: 'from-amber-400 to-orange-500', show: isAdmin },
    { label: t('nav_staff_directory'), icon: Users, path: '/dashboard/employees', gradient: 'from-blue-500 to-indigo-500', show: isAdmin },
    { label: t('nav_analytics'), icon: BarChart, path: '/dashboard/analytics', gradient: 'from-rose-400 to-pink-500', show: isAdmin },
    { label: t('nav_announcements'), icon: Megaphone, path: '/dashboard/announcements', gradient: 'from-amber-400 to-orange-500', show: role === 'school_admin' },
    { label: t('nav_leave_policies'), icon: Settings, path: '/dashboard/leave-config', gradient: 'from-slate-500 to-slate-600', show: role === 'school_admin' },
  ].filter(a => a.show);

  return (
    <div className="glass-card p-5" data-testid="quick-actions">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-4 w-4 text-amber-500" />
        <h2 className="text-sm font-bold text-slate-700">Quick Actions</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {actions.map((action, i) => {
          const Icon = action.icon;
          return (
            <Link key={i} to={action.path}
              className="group flex flex-col items-center gap-2.5 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-teal-200 hover:bg-teal-50/50 hover:shadow-sm transition-all cursor-pointer"
              data-testid={`quick-action-${i}`}
            >
              <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-semibold text-slate-600 text-center leading-tight group-hover:text-teal-700 transition-colors">{action.label}</span>
            </Link>
          );
        })}
        <button onClick={onChangePwd}
          className="group flex flex-col items-center gap-2.5 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-teal-200 hover:bg-teal-50/50 hover:shadow-sm transition-all cursor-pointer"
        >
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
            <KeyRound className="h-5 w-5 text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-600 text-center leading-tight group-hover:text-teal-700 transition-colors">Change Password</span>
        </button>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      toast.success('Password changed successfully');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-teal-500" />
            <h2 className="text-base font-bold text-slate-900">Change Password</h2>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[
            { label: 'Current Password', field: 'current_password', showKey: 'current' },
            { label: 'New Password', field: 'new_password', showKey: 'new' },
            { label: 'Confirm New Password', field: 'confirm_password', showKey: 'confirm' },
          ].map(({ label, field, showKey }) => (
            <div key={field}>
              <label className="text-xs text-slate-500 font-medium">{label}</label>
              <div className="relative mt-1">
                <input
                  type={show[showKey] ? 'text' : 'password'}
                  value={form[field]}
                  onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  required
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                <button type="button" onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show[showKey] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium disabled:opacity-60">
              {loading ? 'Saving...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const isAdmin = user?.role === 'school_admin' || user?.role === 'hr';

  useEffect(() => {
    if (!user) return;
    if (isAdmin) fetchAdminStats();
    else fetchUserBalance();
  }, [user, isAdmin]);

  const fetchAdminStats = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setStats(response.data.data);
    } catch (error) {
      console.error('Dashboard stats fetch failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBalance = async () => {
    try {
      const response = await api.get('/leave/balance');
      setStats(response.data.data);
    } catch (error) {
      console.error('Leave balance fetch failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  const adminCards = [
    { title: t('total_staff'), value: stats?.total_users || 0, sub: `${stats?.active_users || 0} ${t('active')}`, icon: Users, gradient: 'from-teal-500 to-emerald-500' },
    { title: t('pending_requests'), value: stats?.pending_requests || 0, sub: t('awaiting_approval'), icon: Clock, gradient: 'from-amber-400 to-orange-500' },
    { title: t('approved'), value: stats?.approved_this_month || 0, sub: t('this_month'), icon: CheckCircle, gradient: 'from-emerald-500 to-green-500' },
    { title: t('rejected'), value: stats?.rejected_this_month || 0, sub: t('this_month'), icon: XCircle, gradient: 'from-rose-400 to-pink-500' },
  ];

  const leaveTypeColors = {
    sick_leave: { bg: 'bg-rose-50', border: 'border-rose-200', accent: 'text-rose-600', bar: 'bg-rose-400', label: 'Sick Leave' },
    casual_leave: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600', bar: 'bg-blue-400', label: 'Casual Leave' },
    vacation: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600', bar: 'bg-amber-400', label: 'Vacation' },
    earned_leave: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-600', bar: 'bg-emerald-400', label: 'Earned Leave' },
    maternity_leave: { bg: 'bg-pink-50', border: 'border-pink-200', accent: 'text-pink-600', bar: 'bg-pink-400', label: 'Maternity' },
    paternity_leave: { bg: 'bg-indigo-50', border: 'border-indigo-200', accent: 'text-indigo-600', bar: 'bg-indigo-400', label: 'Paternity' },
    unpaid_leave: { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'text-slate-600', bar: 'bg-slate-400', label: 'Unpaid' },
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-500/20">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="dashboard-title">
            {t('welcome_back')}, <span className="text-teal-600">{user?.full_name}</span>
          </h1>
          <p className="text-sm text-slate-500" data-testid="dashboard-subtitle">
            {isAdmin ? t('school_overview') : t('your_leave_balance')}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <QuickActions isAdmin={isAdmin} role={user?.role} onChangePwd={() => setShowChangePwd(true)} />
      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}

      {/* School Code Card for Admins */}
      {isAdmin && stats?.school_code && (
        <div className="glass-card p-4 my-4 flex items-center justify-between" data-testid="school-code-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md">
              <School className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">School Code</p>
              <p className="text-lg font-bold font-mono tracking-[0.1em] text-slate-800" data-testid="dashboard-school-code">{stats.school_code}</p>
            </div>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(stats.school_code);
              toast.success('School code copied!');
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors text-sm text-slate-600"
            data-testid="copy-school-code-dashboard"
          >
            <Copy className="h-4 w-4" />
            <span className="hidden sm:inline">Copy</span>
          </button>
        </div>
      )}

      {isAdmin ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats">
          {adminCards.map((card, idx) => {
            const Icon = card.icon;
            return (
              <div key={idx} className="stat-card p-5 relative overflow-hidden group" data-testid={`stat-card-${idx}`}>
                <div className={`absolute top-0 right-0 w-24 h-24 rounded-full bg-gradient-to-br ${card.gradient} opacity-[0.07] -translate-y-6 translate-x-6 group-hover:opacity-[0.12] transition-opacity`} />
                <div className="flex items-center justify-between mb-4 relative">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{card.title}</p>
                  <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-lg shadow-slate-200`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-800 mb-1 relative">{card.value}</p>
                <p className="text-xs text-slate-400 relative">{card.sub}</p>
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="user-balance">
          <div className="glass-card p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-5" data-testid="balance-title">
              {t('your_leave_balance_title')} <span className="text-teal-500 font-normal text-base">({stats?.year})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats?.balances?.map((balance) => {
                const colors = leaveTypeColors[balance.leave_type] || leaveTypeColors.casual_leave;
                const usedPct = balance.total_allocated > 0 ? (balance.used / balance.total_allocated) * 100 : 0;
                return (
                  <div key={balance.leave_type} className={`p-4 rounded-xl ${colors.bg} border ${colors.border}`} data-testid={`balance-${balance.leave_type}`}>
                    <h3 className={`text-sm font-bold ${colors.accent} mb-3 uppercase tracking-wider`}>
                      {colors.label || balance.leave_type.replace('_', ' ')}
                    </h3>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="text-3xl font-bold text-slate-800">{balance.available}</span>
                      <span className="text-sm text-slate-400">/ {balance.total_allocated}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/80 overflow-hidden mb-3">
                      <div className={`h-full rounded-full ${colors.bar} transition-all`} style={{ width: `${Math.min(usedPct, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>{t('used')}: {balance.used}</span>
                      <span className="text-amber-600 font-medium">{t('pending')}: {balance.pending}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
